import { createApi } from "@reduxjs/toolkit/query/react";
import axiosBaseQuery from "../axiosBaseQuery";
import {
    UseCase,
    MigrationPayloadType,
    Team,
    TeamsPayload,
} from "@callveriq/type/Team";
import { DeleteTeam } from "@callveriq/type/Team";
import { UpdateUserPayload, UserType } from "@callveriq/type/User";
import { isDefined } from "@callveriq/utils/helper/common.helper";

export type TeamMembersResponse = {
    count: number;
    next: string | null;
    previous: string | null;
    results: UserType[];
};

export const teamManagerSeviceSlice = createApi({
    reducerPath: "teamManagerSeviceSlice",
    baseQuery: axiosBaseQuery({
        transformResponse: (response) => response,
    }),
    tagTypes: ["Teams"],
    endpoints: (builder) => ({
        getTeams: builder.query<
            Team[],
            { is_restricted?: boolean; include_members?: boolean } | void
        >({
            // include_members is opt-in: the nested member lists are what made this
            // response multi-MB on large tenants. Only the team-manager screens,
            // which render members directly, ask for them.
            query: (args) =>
                `/person/team/list_all/?is_restricted=${
                    args?.is_restricted ? args.is_restricted : "true"
                }${args?.include_members ? "&include_members=true" : ""}`,
            providesTags: ["Teams"],
        }),
        /**
         * Members of one team - paginated and searchable.
         * The team list no longer nests members (that payload reached 1MB+ on
         * large tenants), so member drawers fetch them per team from here.
         */
        getTeamMembers: builder.query<
            TeamMembersResponse,
            {
                teamId: number;
                search?: string;
                page?: number;
                page_size?: number;
                is_coaching_request?: boolean;
            }
        >({
            query: ({
                teamId,
                search,
                page = 1,
                page_size = 25,
                is_coaching_request,
            }) => ({
                url: `/person/team/${teamId}/members/`,
                method: "GET",
                params: {
                    page,
                    page_size,
                    ...(search ? { search } : {}),
                    ...(is_coaching_request
                        ? { is_coaching_request: true }
                        : {}),
                },
            }),
            serializeQueryArgs: ({ queryArgs, endpointName }) => {
                const { page: _page, ...rest } = queryArgs;
                return `${endpointName}-${JSON.stringify(rest)}`;
            },
            merge: (currentCache, newItems, { arg }) => {
                if ((arg.page ?? 1) <= 1) return newItems;
                return {
                    ...newItems,
                    results: [...currentCache.results, ...newItems.results],
                };
            },
            forceRefetch: ({ currentArg, previousArg }) =>
                currentArg?.page !== previousArg?.page,
            transformResponse: (
                response: TeamMembersResponse | UserType[]
            ): TeamMembersResponse =>
                Array.isArray(response)
                    ? {
                          count: response.length,
                          next: null,
                          previous: null,
                          results: response,
                      }
                    : response,
            providesTags: ["Teams"],
        }),

        getCoachingApplicableTeams: builder.query<Team[], void>({
            query: () => "coachings/teams/eligible/",
        }),

        getUseCase: builder.query<UseCase[], void>({
            query: () => "/person/team/use_case/",
            transformResponse: (response: UseCase[]) =>
                response ? response : [],
        }),
        addUseCase: builder.mutation<UseCase, Partial<UseCase>>({
            query: (payload) => ({
                url: "/person/team/use_case/",
                method: "POST",
                body: payload,
            }),
        }),
        deleteUseCase: builder.mutation<UseCase, number>({
            query: (id) => ({
                url: `/person/team/use_case/${id}`,
                method: "DELETE",
            }),
        }),

        getTeamById: builder.query<Team, number>({
            query: (id) => `/person/team/${id}/`,
            // providesTags: ["Teams"],
        }),

        createTeams: builder.mutation<Team, Partial<Omit<TeamsPayload, "id">>>({
            query: (payload) => ({
                url: "/person/team/create/",
                method: "POST",
                body: payload,
            }),
            async onQueryStarted(_, { queryFulfilled, dispatch }) {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(
                        teamManagerSeviceSlice.util.updateQueryData(
                            "getTeams",
                            undefined,
                            (draft) => {
                                return [data, ...draft];
                            }
                        )
                    );
                } catch {}
            },
        }),

        updateTeam: builder.mutation<Team, Partial<TeamsPayload>>({
            query: ({ id, ...payload }) => ({
                url: `/person/team/edit/${id}`,
                method: "PATCH",
                body: { ...payload, id },
            }),
            async onQueryStarted(_, { queryFulfilled, dispatch }) {
                try {
                    const { data } = await queryFulfilled;
                    const isSubTeam = isDefined(data.group);

                    dispatch(
                        teamManagerSeviceSlice.util.updateQueryData(
                            "getTeams",
                            undefined,
                            (draft) => {
                                let teamToUpdate = isSubTeam
                                    ? draft.find((e) => e.id === data.group)
                                    : draft.find((e) => e.id === data.id);
                                if (isDefined(teamToUpdate)) {
                                    if (isSubTeam) {
                                        teamToUpdate = {
                                            ...teamToUpdate,
                                            subteams: teamToUpdate.subteams.map(
                                                (e) =>
                                                    e.id === data.id ? data : e
                                            ),
                                        };
                                    } else {
                                        teamToUpdate = {
                                            ...data,
                                        };
                                    }
                                    return draft.map((e) =>
                                        e.id === teamToUpdate?.id
                                            ? teamToUpdate
                                            : e
                                    );
                                }
                                return draft;
                            }
                        )
                    );
                } catch {}
            },
            invalidatesTags: (_, err) => (!!err ? [] : ["Teams"]),
        }),

        deleteTeam: builder.mutation<
            DeleteTeam,
            { id: number; data: MigrationPayloadType[] }
        >({
            query: ({ id, data }) => ({
                url: `/person/team/edit/${id}`,
                method: "DELETE",
                body: data,
            }),
            invalidatesTags: (res, error) => (!!error ? [] : ["Teams"]),
        }),

        updateUserTeamById: builder.mutation<
            UserType,
            Partial<UpdateUserPayload> & { id: number }
        >({
            query: ({ id, ...payload }) => ({
                url: `/person/person/retrieve_update/${id}`,
                method: "PATCH",
                body: payload,
            }),
            invalidatesTags: (res, error) => (!!error ? [] : ["Teams"]),
        }),
    }),
});

export const {
    useGetTeamsQuery,
    useGetTeamMembersQuery,
    useLazyGetTeamMembersQuery,
    useLazyGetTeamsQuery,
    useCreateTeamsMutation,
    useUpdateTeamMutation,
    useDeleteTeamMutation,
    useUpdateUserTeamByIdMutation,
    useGetCoachingApplicableTeamsQuery,
    useGetUseCaseQuery,
    useAddUseCaseMutation,
    useDeleteUseCaseMutation,
} = teamManagerSeviceSlice;
